import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip as MuiTooltip,
	Typography,
} from '@mui/material';
import moment from 'moment';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { FaChevronDown, FaChevronUp, FaSyncAlt, FaTrophy } from 'react-icons/fa';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import constants from '../../constants';
import { httpClient } from '../../providers/myDataProvider';

// GH-664 — the step-competition operations dashboard (spec §6). Pick a competition → KPI cards,
// weekly steps trend, and the unit leaderboard with all sub-scores. Generic across companies.

type Competition = {
	_id: string;
	name: string;
	status: string;
	enabled: boolean;
	company: string;
	company_name?: string | null;
	start_date?: string;
	end_date?: string;
	competing_level?: string;
	league_thresholds?: number[];
};

type LeaderRow = {
	unit: string;
	name: string;
	rank: number;
	score: number;
	participation: number;
	movement: number;
	persistence: number;
	improvement: number;
	registered: number;
	potential_employees: number;
	total_steps: number;
	active_participant_days?: number;
	avg_steps_per_active_day: number;
};

type Dashboard = {
	competition: Competition;
	kpis: { registered: number; potential_employees: number; join_rate: number; total_steps: number; units: number };
	leaderboard: LeaderRow[];
	computed_at: string | null;
	weekly_trend: { iso_week: number; total_steps: number; from?: string; to?: string }[];
};

// GH-789 — a wellness lead: a registered member scoped to ONE unit (by name) of the company's current competition.
type LeadRow = {
	member_id: string;
	first_name?: string;
	last_name?: string;
	phone?: string;
	email?: string;
	role?: 'lead' | 'viewer'; // GH-846 — a viewer has no unit (the district ranking only)
	unit_name: string | null;
	unit_found: boolean; // false = the name no longer resolves in the company's current competition
	set_at?: string | null;
};

// GH-846 — the Select value for "צופה בלבד" (no unit); never a real unit name
const VIEWER_ONLY = '__viewer_only__';

type UnitNode = { _id: string; name: string; parent?: string | null };

type Telemetry = {
	total: number;
	no_report: number;
	unsupported: number;
	granted: number;
	denied: number;
	not_requested: number;
};

const PURPLE = '#6D28D9';
const pct = (n: number) => `${Math.round((n || 0) * 100)}%`;
const num = (n: number) => (n || 0).toLocaleString();
const medal = (rank: number) => (rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`);
/**
 * Label a weekly bar by the real days it covers ("16–19.8") rather than by its position in the chart.
 * A bucket is a calendar week, so the first/last week of a competition is usually partial — a running
 * "שבוע 1/2" index reads as "week 1 of the competition" and is simply wrong.
 */
const weekLabel = (w: { from?: string; to?: string }, i: number) => {
	if (!w.from || !w.to) return `שבוע ${i + 1}`;
	const [, fm, fd] = w.from.split('-');
	const [, tm, td] = w.to.split('-');
	const d = (x: string) => String(Number(x));
	return w.from === w.to
		? `${d(fd)}.${d(fm)}`
		: fm === tm
			? `${d(fd)}–${d(td)}.${d(tm)}`
			: `${d(fd)}.${d(fm)}–${d(td)}.${d(tm)}`;
};

// The movement arithmetic with the unit's own numbers — shared by the score tooltip and the movement column's.
// `adjust` is the cap/shrinkage note, only when the shown % differs from the raw average.
const movementMath = (u: LeaderRow, targetSteps?: number, kShrinkage?: number, movementCap?: number | null) => {
	const target = targetSteps || 10000;
	// null/undefined = a backend that doesn't send the denominator yet (NOT "no activity"): fall back
	// to the average alone rather than claiming the unit never walked.
	const days = typeof u.active_participant_days === 'number' ? u.active_participant_days : null;
	// The stored movement is the SHRUNK (or bonus-capped) mean over the target, so the raw average alone would
	// not reproduce the shown %. Derive the adjusted mean back out of it and show both when they differ.
	const adjusted = Math.round((u.movement || 0) * target);
	const raw = Math.round(u.avg_steps_per_active_day || 0);
	const drift = raw > 0 ? Math.abs(adjusted - raw) / raw : 0;
	const noActivity = days === 0 || (days === null && raw === 0);
	// GH-825 — with shrinkage off, a gap between the average and the % means the bonus cap held the unit back.
	const atBonusCap = typeof movementCap === 'number' && movementCap > 1 && (u.movement || 0) >= movementCap - 1e-6 && raw > adjusted;
	return {
		noActivity,
		division: days ? `${num(u.total_steps)} צעדים ÷ ${num(days)} ימי הליכה בפועל` : '',
		average: noActivity ? '' : `${num(raw)} צעדים בממוצע ליום`,
		adjust:
			drift > 0.02 && !noActivity && atBonusCap
				? `תקרת הבונוס ${pct(movementCap as number)} (= ${num(adjusted)})`
				: drift > 0.02 && !noActivity && kShrinkage !== 0
					? `אחרי ריכוך: ${num(adjusted)}`
					: '',
		// GH-825 — a competition may allow a bonus above the target (movement > 100%, up to its cap).
		target:
			(u.movement || 0) > 1.0005
				? `מעל היעד ${num(target)} → ${pct(u.movement)} (בונוס)`
				: (u.movement || 0) >= 1
					? `הגיע ליעד ${num(target)} → 100%`
					: `מתוך יעד ${num(target)} → ${pct(u.movement)}`,
	};
};

// Dynamic score breakdown for the tooltip — weight × component per row, from the unit's own data.
// Weights come from the competition (getDashboard exposes them); default to the 25/50/25 model.
// GH-829 (Hadar's format): every heading carries its weight, on its own bold line with its numbers inset under
// it (Uri: the headings must stand out from what follows); movement reads top-down — the arithmetic first,
// its points LAST.
type BreakdownLine = { text: string; kind: 'heading' | 'line' | 'total' };
const scoreBreakdown = (
	u: LeaderRow & { eligible_participant_days?: number },
	weights?: { participation: number; movement: number; persistence: number },
	persistenceFromStart?: boolean,
	targetSteps?: number,
	kShrinkage?: number,
	movementCap?: number | null,
): BreakdownLine[] => {
	const w = weights || { participation: 25, movement: 50, persistence: 25 };
	const wSum = w.participation + w.movement + w.persistence || 1;
	const heading = (label: string, weight: number): BreakdownLine => ({
		text: `${label} – ${Math.round((weight / wSum) * 100)}%:`,
		kind: 'heading',
	});
	const math = (weight: number, value: number) =>
		`${pct(value)} × ${Math.round((weight / wSum) * 100)}% = ${Math.round(((100 * weight * (value || 0)) / wSum) * 10) / 10} נק׳`;
	const line = (text: string): BreakdownLine => ({ text, kind: 'line' });
	// `detail` (GH-825): the counts behind the % — "3 מתוך 77", "51 ימי הליכה מתוך 90 אפשריים".
	const block = (label: string, weight: number, value: number, detail?: string) => [
		heading(label, weight),
		line(`${detail ? `${detail} = ` : ''}${math(weight, value)}`),
	];
	const m = movementMath(u, targetSteps, kShrinkage, movementCap);
	const joinDetail =
		typeof u.registered === 'number' && typeof u.potential_employees === 'number' && u.potential_employees > 0
			? `${num(u.registered)} מתוך ${num(u.potential_employees)}`
			: undefined;
	// Exact only when everyone shares one window (persistence from the competition start). GH-829 (Hadar): spell
	// out where both numbers come from — walking days = every day a participant walked, all participants together;
	// possible = participants × competition days so far.
	const possible = u.eligible_participant_days;
	const walked = u.active_participant_days || 0;
	const compDays = u.registered > 0 && typeof possible === 'number' ? Math.round(possible / u.registered) : 0;
	const persistLines =
		persistenceFromStart && typeof possible === 'number' && possible > 0
			? [
					line(`${num(walked)} ימי הליכה = כל יום שבו משתתף הלך, של כל המשתתפים יחד`),
					line(compDays ? `${num(possible)} אפשריים = ${num(u.registered)} משתתפים × ${num(compDays)} ימים` : ''),
					line(`${num(walked)} מתוך ${num(possible)} → ${pct(u.persistence)}`),
				]
			: [];
	return [
		...block('השתתפות', w.participation, u.participation, joinDetail),
		heading('תנועה', w.movement),
		line(m.division && m.average ? `${m.division} = ${m.average}` : m.division || m.average),
		line(m.adjust),
		line(m.target),
		line(math(w.movement, u.movement)),
		// Persistence reads the same way: the counts, the %, and the points LAST.
		heading('התמדה', w.persistence),
		...persistLines,
		line(math(w.persistence, u.persistence)),
		{ text: `סה״כ: ${Math.round(u.score)} נק׳`, kind: 'total' as const },
	].filter((l) => l.text);
};
/**
 * The movement % explained with the unit's OWN numbers instead of prose: the actual division
 * (steps ÷ walking days), the shrunk/capped mean when it differs, and the target it is measured
 * against. HR kept asking whether raw steps count at all — this shows exactly where they enter.
 */
const movementBreakdown = (
	u: LeaderRow,
	targetSteps?: number,
	dailyCap?: number,
	kShrinkage?: number,
	movementCap?: number | null,
) => {
	const m = movementMath(u, targetSteps, kShrinkage, movementCap);
	const lines = [
		m.noActivity ? 'אין עדיין ימי פעילות במחוז/מטה' : '',
		m.division,
		m.average ? `= ${m.average}` : '',
		m.adjust,
		m.target,
		// The shrinkage note only when the competition still uses it (GH-825: Maccabi turned it off).
		`${kShrinkage === 0 ? '' : 'ריכוך מקרב מחוזות/מטות קטנים לממוצע הכללי · '}תקרה ${num(dailyCap || 35000)} צעדים ליום`,
	];
	return lines.filter(Boolean);
};

// Display-only label for Competition.competing_level — the DB keeps the raw unit level values
// (default 'district'; free-form, so Hebrew values may appear too); only the wording shown to admins
// changes (GH-767).
const LEVEL_LABEL: Record<string, string> = {
	district: 'מחוז/מטה',
	region: 'יחידה ארגונית',
	מחוז: 'מחוז/מטה',
	מרחב: 'יחידה ארגונית',
};
const levelLabel = (level?: string) => (level ? LEVEL_LABEL[level] || level : '');

// GH-790 — the size leagues a thresholds list produces, as admins read them: [70,150] → "עד 69 · 70–149 · 150 ומעלה".
// Empty = the competition has no leagues.
const leaguesLabel = (thresholds?: number[]) => {
	const t = Array.from(new Set((thresholds || []).map((n) => Math.floor(Number(n))).filter((n) => n > 0))).sort((a, b) => a - b);
	if (!t.length) return '';
	return [...t.map((n, i) => (i === 0 ? `עד ${n - 1}` : `${t[i - 1]}–${n - 1}`)), `${t[t.length - 1]} ומעלה`].join(' · ');
};
// "70, 150" → [70,150] (positive integers, deduped, ascending). Blank → [] (= no leagues). null = invalid, nothing is sent.
const parseThresholds = (text: string): number[] | null => {
	const parts = text.split(',').map((s) => s.trim()).filter(Boolean);
	if (parts.some((s) => !/^\d+$/.test(s) || Number(s) <= 0)) return null;
	return Array.from(new Set(parts.map(Number))).sort((a, b) => a - b);
};

// GH-789 — the unit tree flattened depth-first for the lead's unit picker: each root (מחוז/מטה), then its
// children indented under it, at any depth. Units whose parent is missing are treated as roots.
const flattenUnits = (units: UnitNode[]) => {
	const ids = new Set(units.map((u) => String(u._id)));
	const byParent = new Map<string, UnitNode[]>();
	units.forEach((u) => {
		const p = u.parent && ids.has(String(u.parent)) ? String(u.parent) : '';
		byParent.set(p, [...(byParent.get(p) || []), u]);
	});
	const out: { id: string; name: string; depth: number }[] = [];
	const seen = new Set<string>(); // guards against a malformed (cyclic) tree
	const walk = (parent: string, depth: number) => {
		[...(byParent.get(parent) || [])]
			.sort((a, b) => a.name.localeCompare(b.name, 'he'))
			.forEach((u) => {
				const id = String(u._id);
				if (seen.has(id)) return;
				seen.add(id);
				out.push({ id, name: u.name, depth });
				walk(id, depth + 1);
			});
	};
	walk('', 0);
	return out;
};

const memberMedal = (i: number) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`);
const PERM: Record<string, { label: string; color: string }> = {
	granted: { label: 'אישר/ה', color: '#16A34A' },
	denied: { label: 'סירב/ה', color: '#DC2626' },
	not_requested: { label: 'טרם נשאל/ה', color: '#D97706' },
	unsupported: { label: 'מכשיר לא נתמך', color: '#6B7280' },
	no_report: { label: 'לא נכנס/ה', color: '#9CA3AF' },
};

const KpiCard = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
	<Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center', borderRadius: 3 }}>
		<Typography variant="h4" sx={{ fontWeight: 700, color: PURPLE }}>
			{value}
		</Typography>
		<Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
			{label}
		</Typography>
		{sub ? (
			<Typography variant="caption" sx={{ color: 'text.disabled' }}>
				{sub}
			</Typography>
		) : null}
	</Paper>
);

/**
 * GH-789 — "מובילות וולנס": assign a registered member of the company (by phone) to one unit of the
 * competition. On the web dashboard she then sees the מחוזות/מטות ranking plus the full breakdown of her
 * own unit only. The leads list is per company (resolved against its current competition); the unit
 * picker comes from the competition selected on this page.
 */
const WellnessLeadsSection = ({ competitionId, companyId }: { competitionId: string; companyId: string }) => {
	const [units, setUnits] = useState<{ id: string; name: string; depth: number }[]>([]);
	const [leads, setLeads] = useState<LeadRow[]>([]);
	const [loadingLeads, setLoadingLeads] = useState(false);
	const [phone, setPhone] = useState('');
	const [unitName, setUnitName] = useState('');
	const [saving, setSaving] = useState<'add' | 'remove' | null>(null); // any non-null disables every button
	const [formError, setFormError] = useState<string | null>(null);
	const [listError, setListError] = useState<string | null>(null);
	const [confirmId, setConfirmId] = useState<string | null>(null); // the row asking "להסיר?"

	// Responses that land after the admin switched competition/company must not overwrite the new one.
	const competitionRef = useRef(competitionId);
	const companyRef = useRef(companyId);
	competitionRef.current = competitionId;
	companyRef.current = companyId;

	const loadLeads = useCallback(async () => {
		if (!companyId) return;
		setLoadingLeads(true);
		try {
			const res = await httpClient(`${constants.API_URL}step-competition/admin/leads?companyId=${companyId}`, {
				method: 'GET',
			});
			if (companyRef.current !== companyId) return;
			setLeads(Array.isArray((res.json as any)?.leads) ? (res.json as any).leads : []);
			setListError(null);
		} catch (e: any) {
			if (companyRef.current === companyId) {
				setListError(e?.response?.data?.message || 'לא הצלחנו לטעון את מובילות הוולנס');
			}
		} finally {
			setLoadingLeads(false);
		}
	}, [companyId]);

	useEffect(() => {
		setLeads([]);
		setListError(null);
		setFormError(null);
		setConfirmId(null);
		loadLeads();
	}, [loadLeads]);

	useEffect(() => {
		setUnits([]);
		setUnitName('');
		if (!competitionId) return;
		httpClient(`${constants.API_URL}step-competition/admin/${competitionId}`, { method: 'GET' })
			.then((res) => {
				if (competitionRef.current !== competitionId) return;
				const list = (res.json as any)?.units;
				setUnits(Array.isArray(list) ? flattenUnits(list) : []);
			})
			.catch(() => {
				if (competitionRef.current === competitionId) setFormError('לא הצלחנו לטעון את יחידות התחרות');
			});
	}, [competitionId]);

	const addLead = async () => {
		const p = phone.trim();
		if (!p || !unitName || saving) return;
		setSaving('add');
		setFormError(null);
		const viewer = unitName === VIEWER_ONLY; // GH-846
		try {
			await httpClient(`${constants.API_URL}step-competition/admin/leads`, {
				method: 'POST',
				body: JSON.stringify(viewer ? { companyId, phone: p, viewer: true } : { companyId, phone: p, unitName }),
			});
			setPhone('');
			await loadLeads();
		} catch (e: any) {
			setFormError(e?.response?.data?.message || 'ההוספה נכשלה');
		} finally {
			setSaving(null);
		}
	};

	const removeLead = async (memberId: string) => {
		setSaving('remove');
		setListError(null);
		try {
			await httpClient(`${constants.API_URL}step-competition/admin/leads/${memberId}?companyId=${companyId}`, {
				method: 'DELETE',
			});
			setConfirmId(null);
			await loadLeads();
		} catch (e: any) {
			setListError(e?.response?.data?.message || 'ההסרה נכשלה');
		} finally {
			setSaving(null);
		}
	};

	return (
		<Paper sx={{ p: 2, mt: 3, borderRadius: 3 }}>
			<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
				מובילות וולנס
			</Typography>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
				מובילת וולנס רואה בדשבורד התחרות באתר את טבלת המחוזות/מטות, ואת הפירוט המלא של היחידה שלה בלבד (בלי
				ליגות). "צופה בלבד" רואה רק את טבלת המחוזות/מטות, בלי לפתוח שום יחידה. אפשר להוסיף רק עמית/ה שכבר
				רשום/ה בחברה. היחידות ברשימה הן של התחרות שנבחרה למעלה.
			</Typography>

			<Stack direction="row" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }} alignItems="center">
				<TextField
					size="small"
					label="טלפון"
					placeholder="050-1234567"
					value={phone}
					onChange={(e) => {
						setPhone(e.target.value);
						setFormError(null);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') addLead();
					}}
					inputProps={{ dir: 'ltr' }}
					disabled={!!saving}
					sx={{ minWidth: 200 }}
				/>
				<Select
					size="small"
					displayEmpty
					value={unitName === VIEWER_ONLY || units.some((u) => u.name === unitName) ? unitName : ''}
					onChange={(e) => {
						setUnitName(e.target.value as string);
						setFormError(null);
					}}
					disabled={!!saving || !units.length}
					sx={{ minWidth: 260 }}
				>
					<MenuItem value="" disabled>
						{units.length ? 'בחר יחידה…' : 'אין יחידות בתחרות'}
					</MenuItem>
					{/* GH-846 — no unit: the district ranking only */}
					<MenuItem value={VIEWER_ONLY} sx={{ fontWeight: 600 }}>
						צופה בלבד (טבלת המחוזות, בלי יחידה)
					</MenuItem>
					{units.map((u) => (
						<MenuItem
							key={u.id}
							value={u.name}
							sx={{ pr: 2 + u.depth * 2.5, fontWeight: u.depth ? 400 : 600 }}
						>
							{u.name}
						</MenuItem>
					))}
				</Select>
				<Button variant="contained" onClick={addLead} disabled={!!saving || !phone.trim() || !unitName}>
					{saving === 'add' ? 'שומר…' : 'הוסף'}
				</Button>
			</Stack>
			{formError ? (
				<Typography variant="body2" color="error" sx={{ mb: 1 }}>
					{formError}
				</Typography>
			) : null}

			{listError ? (
				<Typography variant="body2" color="error" sx={{ mb: 1 }}>
					{listError}
				</Typography>
			) : null}
			{loadingLeads && !leads.length ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={22} />
				</Box>
			) : leads.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
					אין מובילות וולנס בחברה
				</Typography>
			) : (
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>שם</TableCell>
								<TableCell>טלפון</TableCell>
								<TableCell>יחידה</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{leads.map((l) => (
								<TableRow key={l.member_id}>
									<TableCell sx={{ fontWeight: 600 }}>
										{`${l.first_name || ''} ${l.last_name || ''}`.trim() || '—'}
									</TableCell>
									<TableCell>
										<span dir="ltr">{l.phone || '—'}</span>
									</TableCell>
									<TableCell>
										{l.role === 'viewer' ? 'צופה בלבד (טבלת המחוזות)' : l.unit_name}
										{l.unit_found ? null : (
											<Typography
												variant="caption"
												color="warning.main"
												sx={{ display: 'block' }}
											>
												⚠️ היחידה לא קיימת בתחרות הנוכחית
											</Typography>
										)}
									</TableCell>
									<TableCell sx={{ width: 260 }}>
										{confirmId === l.member_id ? (
											<Stack direction="row" spacing={1} alignItems="center">
												<Typography variant="caption">להסיר את ההרשאה?</Typography>
												<Button
													size="small"
													variant="contained"
													color="error"
													disabled={!!saving}
													onClick={() => removeLead(l.member_id)}
												>
													{saving === 'remove' ? 'מסיר…' : 'כן, הסר'}
												</Button>
												<Button
													size="small"
													disabled={!!saving}
													onClick={() => {
														setConfirmId(null);
														setListError(null);
													}}
												>
													ביטול
												</Button>
											</Stack>
										) : (
											<Button
												size="small"
												color="error"
												disabled={!!saving}
												onClick={() => setConfirmId(l.member_id)}
											>
												הסר
											</Button>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Paper>
	);
};

export default function StepCompetitionDashboard() {
	const [competitions, setCompetitions] = useState<Competition[]>([]);
	const [selectedId, setSelectedId] = useState<string>('');
	const [dashboard, setDashboard] = useState<Dashboard | null>(null);
	const [telemetry, setTelemetry] = useState<Telemetry | null>(null);
	const [loading, setLoading] = useState(true);
	const [recomputing, setRecomputing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Two-level drill-down: מחוז → מרחבים (aggregate) → click a מרחב → its members (names).
	const [expandedUnit, setExpandedUnit] = useState<string | null>(null); // open מחוז
	const [subUnits, setSubUnits] = useState<Record<string, any[]>>({}); // מרחבים per מחוז
	const [loadingUnit, setLoadingUnit] = useState<string | null>(null);
	const [expandedMarhav, setExpandedMarhav] = useState<string | null>(null); // open מרחב
	const [unitMembers, setUnitMembers] = useState<Record<string, any[]>>({}); // members per מרחב
	const [loadingMarhav, setLoadingMarhav] = useState<string | null>(null);
	const [companyFilter, setCompanyFilter] = useState<string>('');

	// "תחרות חדשה" dialog — creates a competition for a SPECIFIC company (disabled until the
	// פעיל switch is flipped, so nothing goes live by accident).
	const [createOpen, setCreateOpen] = useState(false);
	const [creating, setCreating] = useState(false);
	const [allCompanies, setAllCompanies] = useState<{ _id: string; english_name: string }[]>([]);
	const [form, setForm] = useState({ companyId: '', name: '', start_date: '', end_date: '' });

	// GH-790 — "ליגות לפי גודל" editor: mirrors the selected competition's thresholds; typing is local until "שמור".
	const [leagueText, setLeagueText] = useState('');
	const [savingLeagues, setSavingLeagues] = useState(false);
	const [leagueSaved, setLeagueSaved] = useState(false);

	const openCreate = async () => {
		setCreateOpen(true);
		if (!allCompanies.length) {
			try {
				const res = await httpClient(`${constants.API_URL}company/names`, { method: 'GET' });
				const list = (res.json as any[]) || [];
				setAllCompanies(list.map((c) => ({ _id: c._id, english_name: c.english_name || c._id })));
			} catch {
				setError('לא הצלחנו לטעון את רשימת החברות');
			}
		}
	};

	const createCompetition = async () => {
		if (!form.companyId || !form.name || !form.start_date || !form.end_date) return;
		setCreating(true);
		try {
			const res = await httpClient(`${constants.API_URL}step-competition/admin`, {
				method: 'POST',
				body: JSON.stringify({
					companyId: form.companyId,
					name: form.name,
					start_date: form.start_date,
					end_date: form.end_date,
					status: 'active',
				}),
			});
			const created = res.json as Competition;
			setCreateOpen(false);
			setForm({ companyId: '', name: '', start_date: '', end_date: '' });
			await loadCompetitions();
			if (created?._id) {
				setCompanyFilter(form.companyId);
				setSelectedId(created._id);
			}
		} catch {
			setError('יצירת התחרות נכשלה');
		} finally {
			setCreating(false);
		}
	};

	// Fetchers are separate from the toggles so "חשב מחדש" can refill an ALREADY-OPEN drill-down.
	// The toggles cache (open → fetch once); a recompute must bypass that cache, or the open מרחב keeps
	// showing pre-recompute numbers next to live member rows — exactly the gap it was meant to close.
	const fetchSubUnits = useCallback(async (unitId: string) => {
		setLoadingUnit(unitId);
		try {
			const res = await httpClient(`${constants.API_URL}step-competition/admin/unit/${unitId}/sub-units`, { method: 'GET' });
			setSubUnits((prev) => ({ ...prev, [unitId]: Array.isArray((res.json as any)?.sub_units) ? (res.json as any).sub_units : [] }));
		} catch {
			setSubUnits((prev) => ({ ...prev, [unitId]: [] }));
		} finally {
			setLoadingUnit(null);
		}
	}, []);

	const fetchMembers = useCallback(async (marhavId: string) => {
		setLoadingMarhav(marhavId);
		try {
			const res = await httpClient(`${constants.API_URL}step-competition/admin/unit/${marhavId}/members`, { method: 'GET' });
			setUnitMembers((prev) => ({ ...prev, [marhavId]: Array.isArray((res.json as any)?.members) ? (res.json as any).members : [] }));
		} catch {
			setUnitMembers((prev) => ({ ...prev, [marhavId]: [] }));
		} finally {
			setLoadingMarhav(null);
		}
	}, []);

	// מחוז → its מרחבים (aggregate breakdown).
	const toggleUnit = async (unitId: string) => {
		if (expandedUnit === unitId) {
			setExpandedUnit(null);
			return;
		}
		setExpandedUnit(unitId);
		setExpandedMarhav(null);
		if (!subUnits[unitId]) await fetchSubUnits(unitId);
	};

	// מרחב → its members (names).
	const toggleMarhav = async (marhavId: string) => {
		if (expandedMarhav === marhavId) {
			setExpandedMarhav(null);
			return;
		}
		setExpandedMarhav(marhavId);
		if (!unitMembers[marhavId]) await fetchMembers(marhavId);
	};

	const loadCompetitions = useCallback(async () => {
		try {
			const res = await httpClient(`${constants.API_URL}step-competition/admin`, { method: 'GET' });
			const list = (res.json as Competition[]) || [];
			setCompetitions(list);
			// Functional update: this callback has no deps, so a plain `selectedId` here is the FIRST render's ''
			// and every reload (e.g. after saving leagues) would jump the selection to the first competition.
			if (list.length) setSelectedId((cur) => cur || list[0]._id);
		} catch {
			setError('לא הצלחנו לטעון את התחרויות');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadDashboard = useCallback(async (id: string) => {
		if (!id) return;
		try {
			const res = await httpClient(`${constants.API_URL}step-competition/admin/${id}/dashboard`, {
				method: 'GET',
			});
			setDashboard(res.json as Dashboard);
		} catch {
			setError('לא הצלחנו לטעון את הדשבורד');
		}
	}, []);

	const loadTelemetry = useCallback(async (companyId?: string) => {
		if (!companyId) {
			setTelemetry(null);
			return;
		}
		try {
			const res = await httpClient(
				`${constants.API_URL}step-competition/admin/device-telemetry?companyId=${companyId}`,
				{ method: 'GET' },
			);
			setTelemetry(res.json as Telemetry);
		} catch {
			setTelemetry(null);
		}
	}, []);

	useEffect(() => {
		loadCompetitions();
	}, [loadCompetitions]);

	useEffect(() => {
		if (selectedId) loadDashboard(selectedId);
	}, [selectedId, loadDashboard]);

	const selectedCompany = competitions.find((c) => c._id === selectedId)?.company;
	useEffect(() => {
		loadTelemetry(selectedCompany);
	}, [selectedCompany, loadTelemetry]);

	// Refill the leagues field whenever the selected competition (or its stored thresholds) changes.
	const leagueKey = (competitions.find((c) => c._id === selectedId)?.league_thresholds || []).join(',');
	useEffect(() => {
		setLeagueText(leagueKey);
	}, [selectedId, leagueKey]);
	useEffect(() => {
		setLeagueSaved(false);
	}, [selectedId]);

	const recompute = async () => {
		if (!selectedId) return;
		setRecomputing(true);
		try {
			await httpClient(`${constants.API_URL}step-competition/admin/${selectedId}/recompute`, {
				method: 'POST',
				body: JSON.stringify({}),
			});
			// Everything the recompute touched, reloaded in place — no manual page refresh: the dashboard
			// itself, the cached drill-downs (dropped so a later click refetches), whatever is open right
			// now, and the device telemetry.
			setSubUnits({});
			setUnitMembers({});
			await loadDashboard(selectedId);
			await Promise.all([
				expandedUnit ? fetchSubUnits(expandedUnit) : Promise.resolve(),
				expandedMarhav ? fetchMembers(expandedMarhav) : Promise.resolve(),
				loadTelemetry(selectedCompany),
			]);
		} catch {
			setError('החישוב נכשל');
		} finally {
			setRecomputing(false);
		}
	};

	const toggleEnabled = async (enabled: boolean) => {
		if (!selectedId) return;
		try {
			await httpClient(`${constants.API_URL}step-competition/admin/${selectedId}/enabled`, {
				method: 'POST',
				body: JSON.stringify({ enabled }),
			});
			setCompetitions((prev) => prev.map((c) => (c._id === selectedId ? { ...c, enabled } : c)));
			setDashboard((prev) => (prev ? { ...prev, competition: { ...prev.competition, enabled } } : prev));
		} catch {
			setError('עדכון הסטטוס נכשל');
		}
	};

	// GH-790 — save the size-league thresholds on the competition. Only the thresholds are stored here;
	// the league ranking itself is produced by the next scoring run (cron or "חשב מחדש").
	const saveLeagues = async () => {
		if (!selectedId) return;
		const thresholds = parseThresholds(leagueText);
		if (!thresholds) {
			setError('ספי הליגות לא תקינים — מספרים שלמים חיוביים מופרדים בפסיק, למשל 70,150');
			return;
		}
		setSavingLeagues(true);
		try {
			await httpClient(`${constants.API_URL}step-competition/admin/${selectedId}`, {
				method: 'PUT',
				body: JSON.stringify({ league_thresholds: thresholds }),
			});
			// Patch locally first so the captions reflect what was saved even if the reload fails.
			setCompetitions((prev) => prev.map((c) => (c._id === selectedId ? { ...c, league_thresholds: thresholds } : c)));
			await loadCompetitions();
			setLeagueText(thresholds.join(','));
			setLeagueSaved(true);
		} catch {
			setError('שמירת הליגות נכשלה');
		} finally {
			setSavingLeagues(false);
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	const selected = competitions.find((c) => c._id === selectedId);
	const kpis = dashboard?.kpis;

	// Company filter — matters once several companies have competitions. '' = all.
	const companies = Array.from(
		new Map(competitions.filter((c) => c.company).map((c) => [c.company, c.company_name || c.company])).entries(),
	).map(([id, name]) => ({ id, name }));
	const visibleCompetitions = companyFilter
		? competitions.filter((c) => c.company === companyFilter)
		: competitions;
	const pickCompany = (companyId: string) => {
		setCompanyFilter(companyId);
		const inScope = companyId ? competitions.filter((c) => c.company === companyId) : competitions;
		if (inScope.length && !inScope.some((c) => c._id === selectedId)) setSelectedId(inScope[0]._id);
	};

	return (
		<Box sx={{ p: 3, direction: 'rtl' }}>
			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
				<FaTrophy size={26} color={PURPLE} />
				<Typography variant="h5" sx={{ fontWeight: 700 }}>
					תחרות הצעדים
				</Typography>

				{companies.length > 1 ? (
					<Select
						size="small"
						displayEmpty
						value={companyFilter}
						onChange={(e) => pickCompany(e.target.value as string)}
						disabled={savingLeagues}
						sx={{ minWidth: 180 }}
					>
						<MenuItem value="">כל החברות</MenuItem>
						{companies.map((co) => (
							<MenuItem key={co.id} value={co.id}>
								{co.name}
							</MenuItem>
						))}
					</Select>
				) : null}

				{competitions.length > 0 ? (
					<Select
						size="small"
						value={visibleCompetitions.some((c) => c._id === selectedId) ? selectedId : ''}
						onChange={(e) => setSelectedId(e.target.value as string)}
						disabled={savingLeagues}
						sx={{ minWidth: 240 }}
					>
						{visibleCompetitions.map((c) => (
							<MenuItem key={c._id} value={c._id}>
								{c.name}{!companyFilter && c.company_name ? ` — ${c.company_name}` : ''} {c.enabled ? '' : '(כבוי)'}
							</MenuItem>
						))}
					</Select>
				) : (
					<Typography color="text.secondary">אין תחרויות עדיין</Typography>
				)}

				<Box sx={{ flex: 1 }} />

				{selected ? (
					<FormControlLabel
						control={<Switch checked={!!selected.enabled} onChange={(e) => toggleEnabled(e.target.checked)} />}
						label={selected.company_name ? `פעיל עבור ${selected.company_name}` : 'פעיל'}
					/>
				) : null}
				<Button
					variant="outlined"
					startIcon={<FaSyncAlt />}
					onClick={recompute}
					disabled={recomputing || !selectedId}
				>
					{recomputing ? 'מחשב…' : 'חשב מחדש'}
				</Button>
				<Button variant="contained" onClick={openCreate}>
					+ תחרות חדשה
				</Button>
			</Stack>

			<Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>תחרות צעדים חדשה</DialogTitle>
				<DialogContent sx={{ direction: 'rtl' }}>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<Select
							size="small"
							displayEmpty
							value={form.companyId}
							onChange={(e) => setForm((f) => ({ ...f, companyId: e.target.value as string }))}
						>
							<MenuItem value="" disabled>
								בחר חברה…
							</MenuItem>
							{allCompanies.map((co) => (
								<MenuItem key={co._id} value={co._id}>
									{co.english_name}
								</MenuItem>
							))}
						</Select>
						<TextField
							size="small"
							label="שם התחרות"
							value={form.name}
							onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
						/>
						<TextField
							size="small"
							label="תאריך התחלה"
							type="date"
							InputLabelProps={{ shrink: true }}
							value={form.start_date}
							onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
						/>
						<TextField
							size="small"
							label="תאריך סיום"
							type="date"
							InputLabelProps={{ shrink: true }}
							value={form.end_date}
							onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
						/>
						<Typography variant="caption" color="text.secondary">
							נוצרת כבויה — עד שמדליקים את מתג "פעיל" אף אחד בחברה לא רואה אותה. אחרי
							היצירה צריך לטעון עץ יחידות (מחוזות/מטות ← יחידות ארגוניות) דרך סקריפט הייבוא.
						</Typography>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateOpen(false)}>ביטול</Button>
					<Button
						variant="contained"
						onClick={createCompetition}
						disabled={creating || !form.companyId || !form.name || !form.start_date || !form.end_date}
					>
						{creating ? 'יוצר…' : 'צור תחרות'}
					</Button>
				</DialogActions>
			</Dialog>

			{error ? (
				<Chip label={error} color="error" sx={{ mb: 2 }} onDelete={() => setError(null)} />
			) : null}

			{selected ? (
				<Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} alignItems="center">
					<Chip
						label={selected.status}
						color={selected.status === 'active' ? 'success' : 'default'}
						size="small"
					/>
					{selected.start_date ? (
						<Typography variant="body2" color="text.secondary">
							{moment(selected.start_date).format('DD/MM/YYYY')} –{' '}
							{selected.end_date ? moment(selected.end_date).format('DD/MM/YYYY') : ''}
						</Typography>
					) : null}
					<Typography variant="body2" color="text.secondary">
						מתחרים ברמת: {levelLabel(selected.competing_level)}
					</Typography>
					{dashboard?.computed_at ? (
						<Typography variant="caption" color="text.disabled">
							· עודכן {moment(dashboard.computed_at).format('DD/MM HH:mm')}
						</Typography>
					) : null}
				</Stack>
			) : null}

			{/* GH-790 — unit leagues by size: headcount thresholds that split the units under the מחוזות/מטות
			    into leagues. Stored on the competition; the ranking comes from the next scoring run. */}
			{selected ? (
				<Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }} alignItems="flex-start">
					<TextField
						size="small"
						label="ליגות לפי גודל — ספי עובדים"
						helperText="מספרים מופרדים בפסיק, למשל 70,150 = עד 69 / 70–149 / 150 ומעלה. ריק = בלי ליגות"
						value={leagueText}
						onChange={(e) => {
							setLeagueText(e.target.value);
							setLeagueSaved(false);
						}}
						sx={{ minWidth: 320 }}
					/>
					<Button variant="outlined" onClick={saveLeagues} disabled={savingLeagues} sx={{ mt: 0.25 }}>
						{savingLeagues ? 'שומר…' : 'שמור'}
					</Button>
					<Stack sx={{ mt: 1 }}>
						{leaguesLabel(selected.league_thresholds) ? (
							<Typography variant="caption" color="text.secondary">
								ליגות כיום: {leaguesLabel(selected.league_thresholds)}
							</Typography>
						) : null}
						{leagueSaved ? (
							<Typography variant="caption" color="success.main">
								נשמר · ייכנס לתוקף בחישוב הבא
							</Typography>
						) : null}
					</Stack>
				</Stack>
			) : null}

			{/* KPI cards */}
			{kpis ? (
				<Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }} title="מהחישוב האחרון של מנוע הניקוד">
					<KpiCard label="משתתפים רשומים" value={num(kpis.registered)} sub={`מתוך ${num(kpis.potential_employees)}`} />
					<KpiCard label="אחוז הצטרפות" value={pct(kpis.join_rate)} />
					<KpiCard label="סה״כ צעדים" value={num(kpis.total_steps)} />
					<KpiCard label="מחוזות/מטות מתחרים" value={num(kpis.units)} />
				</Stack>
			) : null}

			{/* Device readiness telemetry — collect-only */}
			{telemetry ? (
				<Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
					<Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							מוכנות מכשירים לספירת צעדים
						</Typography>
						<Typography variant="caption" color="text.disabled">
							· איסוף מידע בלבד ({telemetry.total} עובדים)
						</Typography>
					</Stack>
					<Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
						{[
							{ label: 'אישרו הרשאה', value: telemetry.granted, color: '#16A34A' },
							{ label: 'סירבו', value: telemetry.denied, color: '#DC2626' },
							{ label: 'טרם נשאלו', value: telemetry.not_requested, color: '#D97706' },
							{ label: 'מכשיר לא נתמך', value: telemetry.unsupported, color: '#6B7280' },
							{ label: 'לא נכנסו לאפליקציה', value: telemetry.no_report, color: '#9CA3AF' },
						].map((s) => (
							<Box
								key={s.label}
								sx={{
									flex: 1,
									minWidth: 130,
									textAlign: 'center',
									p: 1.5,
									borderRadius: 2,
									border: '1px solid #EEE',
									borderTop: `3px solid ${s.color}`,
								}}
							>
								<Typography variant="h5" sx={{ fontWeight: 700, color: s.color }}>
									{num(s.value)}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{s.label}
								</Typography>
							</Box>
						))}
					</Stack>
				</Paper>
			) : null}

			{/* Weekly steps trend */}
			{dashboard?.weekly_trend?.length ? (
				<Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						צעדים לפי שבוע
					</Typography>
					<Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
						נתונים חיים — לכן הסכום כאן יכול להיות גבוה מ״סה״כ צעדים״ שבכרטיסים
					</Typography>
					<Box sx={{ height: 240, direction: 'ltr' }}>
						<ResponsiveContainer>
							<BarChart data={dashboard.weekly_trend.map((w, i) => ({ name: weekLabel(w, i), steps: w.total_steps }))}>
								<CartesianGrid strokeDasharray="3 3" vertical={false} />
								<XAxis dataKey="name" fontSize={12} />
								<YAxis fontSize={12} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))} />
								<Tooltip formatter={(v: any) => num(v)} />
								<Bar dataKey="steps" fill={PURPLE} radius={[6, 6, 0, 0]} />
							</BarChart>
						</ResponsiveContainer>
					</Box>
				</Paper>
			) : null}

			{/* Unit leaderboard */}
			<Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
				<Box sx={{ p: 2, pb: 1.5 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>טבלת המחוזות/מטות</Typography>
					{/* The table (and the KPIs) come from the scoring snapshot, while the weekly chart and the
					    member rows in the drill-down are read live — so their totals legitimately differ.
					    Saying which is which turns an apparent bug into an understood time difference. */}
					<Typography variant="caption" color="text.disabled">
						מהחישוב האחרון{dashboard?.computed_at ? ` · ${moment(dashboard.computed_at).format('DD/MM HH:mm')}` : ''}
					</Typography>
				</Box>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell align="center">דירוג</TableCell>
								<TableCell>מחוז/מטה</TableCell>
								<TableCell align="center">ציון</TableCell>
								<TableCell align="center">הצטרפות</TableCell>
								<TableCell align="center">תנועה</TableCell>
								<TableCell align="center">התמדה</TableCell>
								<TableCell align="center">שיפור</TableCell>
								<TableCell align="center">משתתפים</TableCell>
								<TableCell align="center">סה״כ צעדים</TableCell>
								<TableCell align="center">ממוצע/יום פעיל</TableCell>
								<TableCell align="center" />
							</TableRow>
						</TableHead>
						<TableBody>
							{(dashboard?.leaderboard || []).map((u) => {
								const uid = String(u.unit);
								const isOpen = expandedUnit === uid;
								const marhavim = subUnits[uid] || [];
								return (
									<Fragment key={uid}>
										<TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleUnit(uid)}>
											<TableCell align="center" sx={{ fontWeight: 700, fontSize: 16 }}>
												{medal(u.rank)}
											</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>{u.name}</TableCell>
											<TableCell align="center" sx={{ fontWeight: 700, color: PURPLE }}>
												<MuiTooltip
													arrow
													placement="left"
													title={
														<div style={{ direction: 'rtl', textAlign: 'right', fontSize: 13, lineHeight: 1.7 }}>
															{scoreBreakdown(
																u,
																(dashboard?.competition as any)?.weights,
																(dashboard?.competition as any)?.persistence_from_start,
																(dashboard?.competition as any)?.target_steps,
																(dashboard?.competition as any)?.k_shrinkage,
																(dashboard?.competition as any)?.movement_cap,
															).map((l, i) => (
																<div
																	key={i}
																	style={
																		l.kind === 'heading'
																			? { fontWeight: 700, marginTop: i === 0 ? 0 : 6 }
																			: l.kind === 'total'
																				? { fontWeight: 700, marginTop: 8 }
																				: { paddingRight: 12, opacity: 0.9 }
																	}
																>
																	{l.text}
																</div>
															))}
														</div>
													}
												>
													<span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>{Math.round(u.score)}</span>
												</MuiTooltip>
											</TableCell>
											<TableCell align="center">{pct(u.participation)}</TableCell>
											<TableCell align="center">
												<MuiTooltip
													arrow
													placement="left"
													title={
														<div style={{ direction: 'rtl', textAlign: 'right', fontSize: 13, lineHeight: 1.7 }}>
															{movementBreakdown(
																u,
																(dashboard?.competition as any)?.target_steps,
																(dashboard?.competition as any)?.daily_step_cap,
																(dashboard?.competition as any)?.k_shrinkage,
																(dashboard?.competition as any)?.movement_cap,
															).map((l, i, arr) => (
																<div key={i} style={i === arr.length - 1 ? { opacity: 0.7, fontSize: 12, marginTop: 4 } : undefined}>
																	{l}
																</div>
															))}
														</div>
													}
												>
													<span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>{pct(u.movement)}</span>
												</MuiTooltip>
											</TableCell>
											<TableCell align="center">{pct(u.persistence)}</TableCell>
											<TableCell align="center" sx={{ color: u.improvement >= 0 ? 'success.main' : 'error.main' }}>
												{u.improvement >= 0 ? '+' : ''}
												{pct(u.improvement)}
											</TableCell>
											<TableCell align="center">
												{u.registered}/{u.potential_employees}
											</TableCell>
											<TableCell align="center">{num(u.total_steps)}</TableCell>
											<TableCell align="center">{num(u.avg_steps_per_active_day)}</TableCell>
											<TableCell align="center" sx={{ color: 'text.secondary' }}>
												{isOpen ? <FaChevronUp /> : <FaChevronDown />}
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell colSpan={11} sx={{ py: 0, border: 0 }}>
												<Collapse in={isOpen} unmountOnExit>
													<Box sx={{ py: 1.5, px: 2, bgcolor: '#F7F9FB', borderRadius: 2, my: 1 }}>
														<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
															יחידות ארגוניות ב{u.name} ({marhavim.length})
														</Typography>
														<Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
															מספרי היחידה הארגונית מהחישוב האחרון · שורות המשתתפים חיות, ולכן יכולות להיות גבוהות יותר
														</Typography>
														{loadingUnit === uid ? (
															<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
																<CircularProgress size={22} />
															</Box>
														) : marhavim.length === 0 ? (
															<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
																אין יחידות ארגוניות
															</Typography>
														) : (
															marhavim.map((s: any) => {
																const mid = String(s.unit);
																const mOpen = expandedMarhav === mid;
																const mem = unitMembers[mid] || [];
																return (
																	<Box key={mid} sx={{ mb: 0.75, bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #EEF0F3', overflow: 'hidden' }}>
																		<Box
																			onClick={() => toggleMarhav(mid)}
																			sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, cursor: 'pointer' }}
																		>
																			<Box>
																				<Typography sx={{ fontWeight: 600 }}>{s.name}</Typography>
																				<Typography variant="caption" color="text.secondary">
																					{s.registered} משתתפים · {num(s.total_steps)} צעדים · ממוצע {num(s.avg_steps_per_active_day)}
																					<span style={{ color: '#9AA3AF' }}> · מהחישוב האחרון</span>
																				</Typography>
																			</Box>
																			{mOpen ? <FaChevronUp /> : <FaChevronDown />}
																		</Box>
																		<Collapse in={mOpen} unmountOnExit>
																			<Box sx={{ px: 1.5, pb: 1 }}>
																				<Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
																					משתתפי {s.name} ({mem.length})
																					<span style={{ fontWeight: 400, color: '#9AA3AF' }}> · נתונים חיים, מעודכנים לרגע זה</span>
																				</Typography>
																				{loadingMarhav === mid ? (
																					<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
																						<CircularProgress size={20} />
																					</Box>
																				) : mem.length === 0 ? (
																					<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
																						אין משתתפים ביחידה ארגונית זו
																					</Typography>
																				) : (
																					<Table size="small">
																						<TableBody>
																							{mem.map((m: any, i: number) => {
																								const perm = PERM[m.permission] || PERM.no_report;
																								return (
																									<TableRow key={i}>
																										<TableCell align="center" sx={{ width: 44, fontWeight: 700 }}>
																											{memberMedal(i)}
																										</TableCell>
																										<TableCell sx={{ fontWeight: 600 }}>
																											{m.name}
																											{m.eligible_for_win ? ' 🏅' : ''}
																										</TableCell>
																										<TableCell sx={{ color: 'text.secondary' }}>
																											{num(m.total_steps)} צעדים · היום {num(m.today_steps)} · אתמול {num(m.yesterday_steps)}
																										</TableCell>
																										<TableCell align="center">
																											<Chip
																												size="small"
																												variant="outlined"
																												label={perm.label}
																												sx={{ color: perm.color, borderColor: perm.color }}
																											/>
																										</TableCell>
																									</TableRow>
																								);
																							})}
																						</TableBody>
																					</Table>
																				)}
																			</Box>
																		</Collapse>
																	</Box>
																);
															})
														)}
													</Box>
												</Collapse>
											</TableCell>
										</TableRow>
									</Fragment>
								);
							})}
							{!dashboard?.leaderboard?.length ? (
								<TableRow>
									<TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
										אין עדיין תוצאות — לחצו "חשב מחדש" לאחר שנרשמו משתתפים וסונכרנו צעדים
									</TableCell>
								</TableRow>
							) : null}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			{/* GH-789 — wellness leads (scoped access to the web competition dashboard) for the selected company. */}
			{selected?.company ? (
				<WellnessLeadsSection competitionId={selected._id} companyId={selected.company} />
			) : null}
		</Box>
	);
}
